import * as React from 'react';

import { withExceptionCatcher } from '@/components/hoc/withExceptionCatcher';
import EventPageLayout from '@/components/pages/event/layout/EventPageLayout';
import { IComponent } from '@/types/_app';
import { Event } from '@/types/ArticleType';
import { EventAgenda } from '@/types/EventAgenda';
import { EventSpeaker } from '@/types/EventSpeaker';
import { useRouter } from 'next/router';
import HomeLayout from '@/components/pages/event/layout/components/home/HomeLayout';
import AgendaLayout from '@/components/pages/event/layout/components/agenda/AgendaLayout';
import CircleSpinner from '@core/components/display/spinners/CircleSpinner';
import SpeakersLayout from '@/components/pages/event/layout/components/speakers/SpeakersLayout';
import PastAttendeesLayout from '@/components/pages/event/layout/components/past-attendees/PastAttendeesLayout';
import SponsorsLayout from '@/components/pages/event/layout/components/sponsors/SponsorsLayout';
import ContactUsLayout from '@/components/pages/event/layout/components/contact-us/ContactUsLayout';
import TicketsLayout from '@/components/pages/event/layout/components/tickets/TicketsLayout';
import fetchEventSection from '@/components/pages/event/utils/fetchEventsSection';
import FaqsLayout from '@/components/pages/event/layout/components/faqs/FaqsLayout';
import slugify from '@core/utils/functions/slugify';
import DynamicPage from '@/components/pages/event/layout/components/dynamic-page/DynamicPage';

export interface EventPageControllerProps {
    event: Event;
    speakers: EventSpeaker[];
    agenda: EventAgenda[];
}

export const EventPageController: IComponent<any> = ({ event, speakers, agenda }: EventPageControllerProps) => {
    const router = useRouter();

    if (!event) {
        return (
            <div className="w-full p-40 flex justify-center">
                <div className="align-middle">
                    <CircleSpinner className="w-10 h-10" />
                </div>
            </div>
        );
    }
    const { tab } = router.query;
    const accentColor = event.customFields.accent_color || '#254cda';

    // Fetch event data
    const pricing = event.customFields?.content?.filter(ev => ev.acf_fc_layout === 'pricing') || [];
    const pastAttendees = fetchEventSection(event.customFields?.content || [], 'participating_companies');
    const sponsorsAll = event.customFields?.content?.filter(ev => ev.acf_fc_layout === 'founding_sponsors') || [];
    const faqs = fetchEventSection(event.customFields?.content || [], 'faq');
    const pages = event.customFields?.pages || [];
    const dynamicTabs = pages.map(page => ({
        title: page.title,
        slug: slugify(page.title),
    }));

    const tabLayoutSelector = (selectedTab?: string | null): React.ReactNode => {
        switch (selectedTab) {
            case 'tickets':
                return <TicketsLayout pricing={pricing} event={event} accentColor={accentColor} />;
            case 'agenda':
                return <AgendaLayout event={event} agenda={agenda} accentColor={accentColor} />;
            case 'speakers':
                return <SpeakersLayout event={event} speakers={speakers} accentColor={accentColor} />;
            case 'past-attendees':
                return <PastAttendeesLayout pastAttendees={pastAttendees} event={event} accentColor={accentColor} />;
            case 'sponsors':
                return <SponsorsLayout event={event} sponsors={sponsorsAll} accentColor={accentColor} />;
            case 'contact-us':
                return <ContactUsLayout event={event} accentColor={accentColor} />;
            case 'faqs':
                return <FaqsLayout faqs={faqs} />;
            default:
                // eslint-disable-next-line no-case-declarations
                const dynamicPage = pages.find(page => slugify(page.title) === selectedTab);
                if (dynamicPage) {
                    return <DynamicPage content={dynamicPage.content} />;
                }

                return (
                    <HomeLayout
                        event={event}
                        pricing={pricing}
                        speakers={speakers}
                        agenda={agenda}
                        accentColor={accentColor}
                    />
                );
        }
    };

    // Hide speakers and agenda if there is none
    const hideTabs = [];
    if (speakers.length === 0) {
        hideTabs.push('speakers');
    }
    if (agenda.length === 0) {
        hideTabs.push('agenda');
    }
    if (pricing.length === 0) {
        hideTabs.push('tickets');
    }
    if (sponsorsAll.length === 0) {
        hideTabs.push('sponsors');
    }
    if (!faqs || !faqs?.faq_category) {
        hideTabs.push('faqs');
    }
    if (!pastAttendees) {
        hideTabs.push('past-attendees');
    }

    return (
        <EventPageLayout dynamicTabs={dynamicTabs} event={event} hideTabs={hideTabs} accentColor={accentColor}>
            {tabLayoutSelector(tab as string | null)}
        </EventPageLayout>
    );
};

export default withExceptionCatcher(EventPageController);
